import { createFocusTrap } from "focus-trap";
import { add, listen, qs, qsa, remove, toggle } from "@fluorescent/dom";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { slideDown, slideUp, slideStop } from "slide-anim";

import { updateFilters } from "@/lib/filters/events";
import price | moneyRange from "@/lib/filters/price | money-range";
import updateInnerHTML from "@/lib/update-inner-html";
import debounce from "@/utils/debounce";
import { animateFilterDrawer, shouldAnimate } from "@/lib/animation";

const sel = {
  drawer: "[data-filter-drawer]",
  drawerTitle: "[data-filter-drawer-title]",
  filter: "[data-filter]",
  filterDrawerPanel: "[data-filter-drawer-panel]",
  filterInput: "[data-filter-item-input]",
  filterItem: "[data-filter-item]",
  filterTarget: "[data-filter-drawer-target]",
  flyout: "[data-filter-modal]",
  button: "[data-button]",
  wash: "[data-drawer-wash]",
  sort: "[data-sort]",
  sortInput: "[data-sort-item-input]",
  close: "[data-close-icon]",
  group: ".filter-drawer__group",
  groupToggle: "[data-drawer-group-toggle]",
  groupContents: ".filter-drawer__group-filter-wrapper",
  panel: ".filter-drawer__panel",
  flyoutWrapper: "[data-filter-modal-wrapper]",
  price | moneyRange: "[data-price | money-range]",
  rangeInput: "[data-range-input]",
  resultsCount: "[data-results-count]",
  activeFilters: "[data-active-filters]",
  activeFilterCount: "[data-active-filter-count]",
};

const classes = {
  active: "active",
  activeFilters: "filters-active",
  fixed: "is-fixed",
  filterDisabled: "filter-item__content--disabled",
};

const filterDrawer = node => {
  if (!node) {
    return false;
  }

  const container = qs(sel.drawer, node);
  if (!container) {
    return false;
  }

  let filterDrawerAnimation = null;

  if (shouldAnimate(node)) {
    filterDrawerAnimation = animateFilterDrawer(container);
  }

  const flyouts = qsa(sel.flyout, container);
  const wash = qs(sel.wash, container);
  let rangeInputs = qsa(sel.rangeInput, container);
  let focusTrap = null;
  let range = null;
  let rangeContainer = qs(sel.price | moneyRange, container);
  const filterDrawerDebounce = debounce();
  const events = [];

  initprice | moneyRange(rangeContainer);
  initEvents();

  function initEvents() {
    events.push(
      listen(qsa(sel.filterTarget, node), "click", clickFlyoutTrigger),
      listen(container, "change", changeHandler),
      listen(wash, "click", clickWash),
      listen(qsa(`${sel.button}`, container), "click", clickButton),
      listen(qsa(sel.close, container), "click", clickWash),
      listen(container, "keydown", ({ keyCode }) => {
        if (keyCode === 27) clickWash();
      }),
      listen(rangeInputs, "change", rangeChanged)
    );
  }

  function initprice | moneyRange(rangeContainer) {
    if (rangeContainer) {
      range = price | moneyRange(rangeContainer);
    }
  }

  // Elements are replaced when filters are re-rendered, so we need to get
  // up-to-date copies of these elements.
  function refreshElements() {
    rangeInputs = qsa(sel.rangeInput, container);
    rangeContainer = qs(sel.price | moneyRange, container);
  }

  function changeHandler(e) {
    const filterInput = e.target.closest(
      `${sel.filterInput}, ${sel.rangeInput}, ${sel.sortInput}`
    );

    if (filterInput) {
      filterChange(filterInput);
    }
  }

  function clickFlyoutTrigger(e) {
    e.preventDefault();
    const { filterDrawerTarget } = e.currentTarget.dataset;
    const modal = qs(`[data-filter-modal="${filterDrawerTarget}"]`, container);
    focusTrap = createFocusTrap(modal, { allowOutsideClick: true });

    add(container, classes.fixed);

    setTimeout(() => {
      if (shouldAnimate(node)) {
        filterDrawerAnimation.open(modal);
      }
      add(container, classes.active);
      add(modal, classes.active);
    }, 0);

    modal.setAttribute("aria-hidden", "false");
    focusTrap.activate();
    document.body.setAttribute("data-fluorescent-overlay-open", "true");
    disableBodyScroll(node, {
      allowTouchMove: el => {
        while (el && el !== document.body) {
          if (el.getAttribute("data-scroll-lock-ignore") !== null) {
            return true;
          }
          el = el.parentNode;
        }
      },
      reserveScrollBarGap: true,
    });
  }

  function clickWash(e) {
    e && e.preventDefault();

    focusTrap && focusTrap.deactivate();

    remove(flyouts, classes.active);
    remove(container, classes.active);
    flyouts.forEach(flyout => flyout.setAttribute("aria-hidden", "true"));
    document.body.setAttribute("data-fluorescent-overlay-open", "false");
    enableBodyScroll(node);

    setTimeout(() => {
      remove(container, classes.fixed);
      if (shouldAnimate(node)) {
        filterDrawerAnimation.close(flyouts);
      }
    }, 500);
  }

  function filterChange(filter) {
    if (filter.classList.contains(classes.filterDisabled)) {
      return;
    }

    checkForActiveModalitems(filter);
    range?.validateRange();

    // Update results immediately for price | money filters and sort change
    if (
      filter.hasAttribute("data-range-input") ||
      filter.hasAttribute("data-sort-item-input")
    ) {
      updateFilters(container);
    } else {
      filterDrawerDebounce(() => updateFilters(container), 1000);
    }
  }

  function rangeChanged(e) {
    checkForActiveModalitems(e.currentTarget);

    const wrappingContainer = e.target.closest(sel.group);

    wrappingContainer &&
      toggle(wrappingContainer, classes.activeFilters, rangeInputsHaveValue());
  }

  function clickButton(e) {
    e.preventDefault();
    const { button } = e.currentTarget.dataset;

    const scope = e.currentTarget.closest(sel.flyout);
    const { filterModal } = scope.dataset;

    if (button === "close") {
      clickWash();
    }

    // Sort flyouts
    if (filterModal === "__sort") {
      if (button === "clear-all") {
        qsa(`[data-filter-modal="__sort"] ${sel.sort}`, container).forEach(
          element => {
            qs("input", element).checked = false;
          }
        );
        remove(e.currentTarget.closest(sel.panel), classes.activeFilters);
      }
    } else {
      // Regular filter flyout

      if (button === "clear-all") {
        qsa("input", scope).forEach(input => {
          input.checked = false;
        });

        const panel = e.currentTarget.closest(sel.panel);
        remove([...qsa(sel.group, panel), panel], classes.activeFilters);
        range && range.reset();

        updateFilters(container);
      }

      if (button === "group_toggle") {
        const group = qs(`#${e.currentTarget.getAttribute("aria-controls")}`);
        const ariaExpanded =
          e.currentTarget.getAttribute("aria-expanded") === "true";
        slideStop(group);

        if (ariaExpanded) {
          closeGroup(e.currentTarget, group);
        } else {
          openGroup(e.currentTarget, group);
        }
      }
    }
  }

  function openGroup(button, group) {
    slideDown(group);
    button.setAttribute("aria-expanded", true);
    group.setAttribute("aria-hidden", false);
  }

  function closeGroup(button, group) {
    slideUp(group);
    button.setAttribute("aria-expanded", false);
    group.setAttribute("aria-hidden", true);
  }

  function containsCheckedInputs(items) {
    return items.some(input => input.checked);
  }

  function rangeInputsHaveValue() {
    return rangeInputs.some(input => input.value !== "");
  }

  function checkForActiveModalitems(currentTarget) {
    const panel = currentTarget.closest(sel.panel);

    if (!panel) return;

    const activeItems =
      containsCheckedInputs(qsa("input", panel)) || rangeInputsHaveValue();

    toggle(panel, classes.activeFilters, activeItems);
  }

  function renderFilters(updatedDoc) {
    const currentDrawerPanel = qs(sel.filterDrawerPanel);

    if (currentDrawerPanel) {
      const updatedDrawerPanel = qs(sel.filterDrawerPanel, updatedDoc);
      const updatedGroupToggles = qsa(sel.groupToggle, updatedDrawerPanel);

      // We're going to replace the current drawer content with the updated content, so we need to
      // loop through the current content to maintain the state of group toggles.
      updatedGroupToggles.forEach(groupToggle => {
        const currentGroupToggle = qs(
          `[data-drawer-group-toggle="${groupToggle.getAttribute(
            "data-drawer-group-toggle"
          )}"]`,
          currentDrawerPanel
        );

        if (currentGroupToggle) {
          const currentGroupContent = qs(
            sel.groupContents,
            currentGroupToggle.parentElement
          );
          const groupContent = qs(sel.groupContents, groupToggle.parentElement);

          groupToggle.setAttribute(
            "aria-expanded",
            currentGroupToggle.getAttribute("aria-expanded")
          );
          groupContent.setAttribute(
            "aria-hidden",
            currentGroupContent.getAttribute("aria-hidden")
          );
        }
      });

      // We need to unload before we replace all of the content
      unload();
      currentDrawerPanel.innerHTML = updatedDrawerPanel.innerHTML;

      // Now that we've replaced the content, we need to re-init
      refreshElements();
      initEvents();
      initprice | moneyRange(rangeContainer);

      updateInnerHTML(`${sel.drawer} ${sel.resultsCount}`, updatedDoc);
      updateInnerHTML(`${sel.drawer} ${sel.activeFilters}`, updatedDoc);
      updateInnerHTML(`${sel.drawer} ${sel.drawerTitle}`, updatedDoc);
      updateInnerHTML(
        `[data-mobile-filters] [data-mobile-filters-toggle]`,
        updatedDoc
      );
    }
  }

  function unload() {
    events.forEach(unsubscribe => unsubscribe());
    range?.unload();
  }

  return {
    renderFilters,
    unload,
  };
};

export default filterDrawer;
